import { lazy, Suspense } from 'react';
import type { ReactNode } from 'react';
import { Navigate, Route, Routes } from 'react-router';
import AppShell from '@components/layout/AppShell';
import ListKeepAlive from '@components/layout/ListKeepAlive';
import { PageLoading } from '@components/layout/PageScaffold';
import { RealtimeProvider } from '@hooks/useRealtime';
import { useAccess } from '@hooks/useAccess';
import HomePage from '@pages/Home/HomePage';
import LcertCounties from '@pages/Lcert/LcertCounties';
import LcertOverview from '@pages/Lcert/LcertOverview';
import NotificationsList from '@pages/Notifications/NotificationsList';
import NotificationView from '@pages/Notifications/NotificationView';
import NotificationEdit from '@pages/Notifications/NotificationEdit';

const LcertView = lazy(() => import('@pages/Lcert/LcertView'));
const LcertForm = lazy(() => import('@pages/Lcert/LcertForm'));
const EquipmentList = lazy(() => import('@pages/Equipment/EquipmentList'));
const EquipmentView = lazy(() => import('@pages/Equipment/EquipmentView'));
const EquipmentForm = lazy(() => import('@pages/Equipment/EquipmentForm'));
const CalibrationView = lazy(() => import('@pages/Equipment/CalibrationView'));
const ExercisesHome = lazy(() => import('@pages/Exercises/ExercisesHome'));
const PlantPage = lazy(() => import('@pages/Exercises/PlantPage'));
const ExerciseView = lazy(() => import('@pages/Exercises/ExerciseView'));
const ExerciseForm = lazy(() => import('@pages/Exercises/ExerciseForm'));
const CapabilityForm = lazy(() => import('@pages/Exercises/CapabilityForm'));
const RedistributionList = lazy(() => import('@pages/Redistribution/RedistributionList'));
const RedistributionView = lazy(() => import('@pages/Redistribution/RedistributionView'));
const RedistributionForm = lazy(() => import('@pages/Redistribution/RedistributionForm'));
const AgreementView = lazy(() => import('@pages/Redistribution/AgreementView'));
const FileLibrary = lazy(() => import('@pages/FileLibrary/FileLibrary'));
const TrainingMap = lazy(() => import('@pages/Training/TrainingMap'));
const TrainingClasses = lazy(() => import('@pages/Training/TrainingClasses'));
const TrainingClassView = lazy(() => import('@pages/Training/TrainingClassView'));
const CapabilityTargetsList = lazy(() => import('@pages/CapabilityTargets/CapabilityTargetsList'));
const CapabilityTargetPage = lazy(() => import('@pages/CapabilityTargets/CapabilityTargetPage'));
const MilestonesList = lazy(() => import('@pages/Milestones/MilestonesList'));
const MilestonePage = lazy(() => import('@pages/Milestones/MilestonePage'));

/** Catalog pages (capability targets, milestone templates): Full access, like the legacy drawer rule. */
const RequireFullState = ({ children }: { children: ReactNode }) => {
  const { isFull } = useAccess();
  return isFull ? <>{children}</> : <Navigate to='/' replace />;
};

/** Legacy lcert Router: state users get the county list, county users their own years overview. */
const LcertHome = () => {
  const { isStateUser } = useAccess();
  return isStateUser ? <LcertCounties /> : <LcertOverview />;
};

const Lazy = ({ children }: { children: ReactNode }) => (
  <Suspense fallback={<PageLoading />}>{children}</Suspense>
);

/**
 * Signed-in route table. Every list is a keep-alive layout route: its view /
 * edit / new children render as overlays while the list (and its realtime
 * subscription) stays mounted underneath.
 */
const AppRoutes = () => (
  <RealtimeProvider>
    <AppShell>
      <Routes>
        <Route path='/' element={<HomePage tab='welcome' />} />
        <Route path='/letters' element={<HomePage tab='letters' />} />
        <Route path='/dashboard' element={<Navigate to='/' replace />} />
        <Route path='/dashboard/letters' element={<Navigate to='/letters' replace />} />

        {/* Annual Letters of Certification */}
        <Route
          path='/lcert'
          element={
            <ListKeepAlive listPath='/lcert'>
              <LcertHome />
            </ListKeepAlive>
          }>
          <Route path='county/:gid' element={<LcertOverview />} />
          <Route path='notifications' element={<NotificationsList domain='lcert' />} />
          <Route
            path='notifications/new'
            element={<NotificationEdit domain='lcert' mode='new' />}
          />
          <Route path='notifications/:id' element={<NotificationView domain='lcert' />} />
          <Route
            path='notifications/:id/edit'
            element={<NotificationEdit domain='lcert' mode='edit' />}
          />
          <Route
            path=':id'
            element={
              <Lazy>
                <LcertView />
              </Lazy>
            }
          />
          <Route
            path=':id/edit'
            element={
              <Lazy>
                <LcertForm />
              </Lazy>
            }
          />
        </Route>

        {/* Equipment + calibration due dates share one list page (tabs) */}
        <Route
          path='/equipment'
          element={
            <ListKeepAlive listPath={['/equipment', '/equipment/calibration']}>
              <Lazy>
                <EquipmentList />
              </Lazy>
            </ListKeepAlive>
          }>
          <Route path='notifications' element={<NotificationsList domain='equipment' />} />
          <Route
            path='notifications/new'
            element={<NotificationEdit domain='equipment' mode='new' />}
          />
          <Route path='notifications/:id' element={<NotificationView domain='equipment' />} />
          <Route
            path='notifications/:id/edit'
            element={<NotificationEdit domain='equipment' mode='edit' />}
          />
          <Route
            path='new'
            element={
              <Lazy>
                <EquipmentForm mode='new' />
              </Lazy>
            }
          />
          <Route
            path='calibration/new'
            element={
              <Lazy>
                <CalibrationView mode='new' />
              </Lazy>
            }
          />
          <Route
            path='calibration/:id'
            element={
              <Lazy>
                <CalibrationView mode='view' />
              </Lazy>
            }
          />
          <Route
            path='calibration/:id/edit'
            element={
              <Lazy>
                <CalibrationView mode='edit' />
              </Lazy>
            }
          />
          <Route
            path=':id'
            element={
              <Lazy>
                <EquipmentView />
              </Lazy>
            }
          />
          <Route
            path=':id/edit'
            element={
              <Lazy>
                <EquipmentForm mode='edit' />
              </Lazy>
            }
          />
        </Route>

        {/* Exercises (requirements): plants map -> plant -> exercises / evaluation areas */}
        <Route
          path='/exercises'
          element={
            <Lazy>
              <ExercisesHome tab='map' />
            </Lazy>
          }
        />
        <Route
          path='/exercises/overview'
          element={
            <Lazy>
              <ExercisesHome tab='overview' />
            </Lazy>
          }
        />
        <Route path='/requirements/*' element={<Navigate to='/exercises' replace />} />
        <Route
          path='/exercises/plants/:plantId'
          element={
            <ListKeepAlive
              listPath={['/exercises/plants/:plantId', '/exercises/plants/:plantId/capabilities']}>
              <Lazy>
                <PlantPage />
              </Lazy>
            </ListKeepAlive>
          }>
          <Route
            path='new'
            element={
              <Lazy>
                <ExerciseForm mode='new' />
              </Lazy>
            }
          />
          <Route
            path='capabilities/new'
            element={
              <Lazy>
                <CapabilityForm mode='new' />
              </Lazy>
            }
          />
          <Route
            path='capabilities/:capabilityId'
            element={
              <Lazy>
                <CapabilityForm mode='view' />
              </Lazy>
            }
          />
          <Route
            path='capabilities/:capabilityId/edit'
            element={
              <Lazy>
                <CapabilityForm mode='edit' />
              </Lazy>
            }
          />
          <Route
            path=':exerciseId'
            element={
              <Lazy>
                <ExerciseView tab='details' />
              </Lazy>
            }
          />
          <Route
            path=':exerciseId/milestones'
            element={
              <Lazy>
                <ExerciseView tab='milestones' />
              </Lazy>
            }
          />
          <Route
            path=':exerciseId/eopa'
            element={
              <Lazy>
                <ExerciseView tab='eopa' />
              </Lazy>
            }
          />
          <Route
            path=':exerciseId/activities'
            element={
              <Lazy>
                <ExerciseView tab='activities' />
              </Lazy>
            }
          />
          <Route
            path=':exerciseId/edit'
            element={
              <Lazy>
                <ExerciseForm mode='edit' />
              </Lazy>
            }
          />
        </Route>

        {/* Redistribution matrix + agreements */}
        <Route
          path='/redistribution'
          element={
            <ListKeepAlive listPath={['/redistribution', '/redistribution/agreements']}>
              <Lazy>
                <RedistributionList />
              </Lazy>
            </ListKeepAlive>
          }>
          <Route
            path='agreements/:contributionId'
            element={
              <Lazy>
                <AgreementView />
              </Lazy>
            }
          />
          <Route
            path='new'
            element={
              <Lazy>
                <RedistributionForm mode='new' />
              </Lazy>
            }
          />
          <Route
            path=':id'
            element={
              <Lazy>
                <RedistributionView />
              </Lazy>
            }
          />
          <Route
            path=':id/edit'
            element={
              <Lazy>
                <RedistributionForm mode='edit' />
              </Lazy>
            }
          />
        </Route>

        <Route
          path='/filelibrary/*'
          element={
            <Lazy>
              <FileLibrary />
            </Lazy>
          }
        />

        {/* Training: status map + classes */}
        <Route
          path='/training'
          element={
            <Lazy>
              <TrainingMap />
            </Lazy>
          }
        />
        <Route
          path='/training/classes'
          element={
            <ListKeepAlive listPath='/training/classes'>
              <Lazy>
                <TrainingClasses />
              </Lazy>
            </ListKeepAlive>
          }>
          <Route
            path=':id'
            element={
              <Lazy>
                <TrainingClassView />
              </Lazy>
            }
          />
        </Route>

        {/* Catalogs (Full-access state users) */}
        <Route
          path='/capabilities'
          element={
            <RequireFullState>
              <ListKeepAlive listPath='/capabilities'>
                <Lazy>
                  <CapabilityTargetsList />
                </Lazy>
              </ListKeepAlive>
            </RequireFullState>
          }>
          <Route
            path='new'
            element={
              <Lazy>
                <CapabilityTargetPage mode='new' />
              </Lazy>
            }
          />
          <Route
            path=':id'
            element={
              <Lazy>
                <CapabilityTargetPage mode='view' />
              </Lazy>
            }
          />
          <Route
            path=':id/edit'
            element={
              <Lazy>
                <CapabilityTargetPage mode='edit' />
              </Lazy>
            }
          />
        </Route>
        <Route
          path='/milestones'
          element={
            <RequireFullState>
              <ListKeepAlive listPath='/milestones'>
                <Lazy>
                  <MilestonesList />
                </Lazy>
              </ListKeepAlive>
            </RequireFullState>
          }>
          <Route
            path='new'
            element={
              <Lazy>
                <MilestonePage mode='new' />
              </Lazy>
            }
          />
          <Route
            path=':id'
            element={
              <Lazy>
                <MilestonePage mode='view' />
              </Lazy>
            }
          />
          <Route
            path=':id/edit'
            element={
              <Lazy>
                <MilestonePage mode='edit' />
              </Lazy>
            }
          />
        </Route>

        <Route path='*' element={<Navigate to='/' replace />} />
      </Routes>
    </AppShell>
  </RealtimeProvider>
);

export default AppRoutes;
